'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { VariantProps } from 'tailwind-variants';

import BigArrowIcon from '@/fo/icons/base/icons/chevron.svg';
import { BreadcrumbsItem } from '@/fo/util/map-product-data';

import { mapToJsonLd } from '../helpers/mapToJsonLd';

import { styles } from './UiKitBreadcrumbsBase.styles';
type NativeElement = React.HTMLAttributes<HTMLDivElement>;

export interface UiKitBreadcrumbsBaseProps {
  className?: string;
  items: BreadcrumbsItem[];
  onClick?: NativeElement['onClick'];
  small?: VariantProps<typeof styles>['small'];
  smallIsBack?: boolean;
  isOutputSchemaJson?: boolean;
  urlData?: {
    baseUrl: string;
    currentUrl: string;
  };
  type?: VariantProps<typeof styles>['type'];
}

export function UiKitBreadcrumbsBase({
  className,
  items,
  onClick,
  small,
  smallIsBack,
  isOutputSchemaJson = true,
  urlData,
  type
}: UiKitBreadcrumbsBaseProps) {
  const slots = styles({ small, type });
  const t = useTranslations();
  const lastItem = items[items.length - 2];

  const withTrailingSlash = (href: string) =>
    href.endsWith('/') ? href : `${href}/`;

  return (
    <nav
      className={slots.base({ className })}
      onClick={onClick}
    >
      {isOutputSchemaJson && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(mapToJsonLd(items, urlData || undefined))
          }}
        />
      )}
      <ol className={slots.list()}>
        {items.map((item, index) => (
          <li
            key={index}
            className={slots.item()}
          >
            {index > 0 && <span className={slots.separator()}>/</span>}
            {item.href ? (
              <Link
                href={withTrailingSlash(item.href)}
                className={slots.link()}
                dangerouslySetInnerHTML={{ __html: item.label }}
              />
            ) : (
              <span dangerouslySetInnerHTML={{ __html: item.label }} />
            )}
          </li>
        ))}
      </ol>
      {lastItem && (
        <div className={slots.mobile()}>
          {lastItem.href ? (
            <Link
              href={withTrailingSlash(lastItem.href)}
              className={slots.link()}
            >
              <BigArrowIcon className={slots.arrow()} />
              {smallIsBack ? t('back') : lastItem.label}
            </Link>
          ) : (
            <div className={slots.link()}>
              {smallIsBack ? t('back') : lastItem.label}
            </div>
          )}
        </div>
      )}
    </nav>
  );
}
